import { queryKeys } from "@/constants/queryKeyFactory";
import { useMutation, useQuery } from "@/hooks/api";
import {
  ApiResponse,
  PaginationParams,
  QueryOptions,
} from "@/interfaces/api.interface";
import { IUserGroup, IUserGroupWithCompany } from "@/interfaces/userGroup.interface";
import { queryClient } from "@/lib/queryClient";
import { userGroupSvc } from "@/services/userGroup.service";

export function useUserGroups(
  params: PaginationParams = {},
  options: QueryOptions = {}
) {
  return useQuery<ApiResponse<IUserGroup[]>>(
    queryKeys.userGroup.list(params),
    () =>
      userGroupSvc.get<IUserGroup>({
        pageNumber: params.pageNumber,
        pageSize: params.pageSize,
        sort: params.sort,
        filter: params.filter,
        searchFilters: params.searchFilters,
      }),
    options
  );
}

export function useAllUserGroups(
  params: PaginationParams = {},
  options: QueryOptions = {}
) {
  return useQuery<ApiResponse<IUserGroup[]>>(
    queryKeys.userGroup.all({ ...params }),
    () => userGroupSvc.getAll<IUserGroup>(params),
    options
  );
}

export function useUserGroupsWithCompanyInfo(
  projectId: string,
  levelId: string,
  options: QueryOptions = {}
) {
  return useQuery<ApiResponse<IUserGroupWithCompany[]>>(
    queryKeys.userGroup.withCompanyInfo(projectId, levelId),
    () => userGroupSvc.getUserGroupsWithCompanyInfo(projectId, levelId),
    options
  );
}

export function useCreateUserGroup(options: QueryOptions = {}) {
  return useMutation<ApiResponse<IUserGroup>, IUserGroup>(
    (data: IUserGroup) => userGroupSvc.create<IUserGroup>(data),
    {
      ...options,
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: queryKeys.userGroup.root,
        });
      },
    }
  );
}

export function useUpdateUserGroup(options: QueryOptions = {}) {
  return useMutation<ApiResponse<IUserGroup>, { id: string; data: IUserGroup }>(
    ({ id, data }) => userGroupSvc.update<IUserGroup>(id, data),
    {
      ...options,
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: queryKeys.userGroup.root,
          refetchType: "active",
        });
      },
    }
  );
}
